import {Alert} from 'components/system/alert';
import {Divider} from 'components/system/divider';
import {Tab, Tabs} from 'components/system/tabs';
import {CheckmarkIcon, CloseIcon, InfoIcon} from 'icons';
import {useMemo, useState} from 'react';
import {classNames} from 'utils';
import {DrawableTable, isDrawableTable} from './drawable_table';
import {isMonteCarloSimulation, MonteCarloSimulation} from './monte_carlo_simulation';
import {OutputBlock} from './output_block';
import styles from './test_case_result.module.css';

// TODO: Show testCase.message when we enhance them to return more specific info about the failure.

export const TestCaseResult = ({index, testCase, collapsed: initialCollapsed = false}: Props) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  const inputTabs = useMemo(() => {
    const tabs: Tab[] = [];
    if (testCase.table_data !== '') {
      try {
        const tables = JSON.parse(testCase.table_data);
        for (const [key, value] of Object.entries(tables)) {
          tabs.push({label: key, children: <DrawableTable data={value as Object[]} />});
        }
      } catch (e) {
        console.log(e);
        return tabs;
      }
      //the tables are in object where the key is the table name and the value is the table
    }
    if (testCase.func_args !== '') {
      try {
        const funcArguments = JSON.parse(testCase.func_args) as string[];
        // arguments are of type string[]
        for (let [index, value] of funcArguments.entries()) {
          if (value.length === 0) {
            continue;
          }
          tabs.push({
            label: `argument #${index + 1}`,
            children: (
              <p>
                {' '}
                {isDrawableTable(JSON.stringify(value)) ? (
                  <DrawableTable data={value} />
                ) : (
                  JSON.stringify(value)
                )}
              </p>
            ),
          });
        }
      } catch (e) {
        console.log(e);
        return tabs;
      }
    }
    return tabs;
  }, []);
  return (
    <div
      className={classNames([styles.container, testCase.success ? styles.success : styles.error])}>
      <div className={styles.header} onClick={() => setCollapsed(!collapsed)}>
        <Divider align='left' size='sm'>
          {testCase.success ? <CheckmarkIcon color='green' /> : <CloseIcon color='red' />}{' '}
          {testCase.playground ? 'Playground' : `Test case #${index + 1}`}{' '}
          <span className={styles.expandCTA}>({collapsed ? 'view' : 'hide'} details)</span>
        </Divider>
      </div>

      {!collapsed && (
        <div className={styles.details}>
          {testCase.stderr && (
            <OutputBlock label='stderr' status='error'>
              {testCase.stderr}
            </OutputBlock>
          )}

          {testCase.stdout && (
            <OutputBlock label='stdout' status='info'>
              {testCase.stdout}
            </OutputBlock>
          )}

          {!testCase.monte_carlo_simulation && (
            <>
              {testCase.output && (
                <OutputBlock
                  label='Your code output'
                  status={testCase.success ? 'passed' : 'failed'}>
                  {isDrawableTable(testCase.output) ? (
                    <DrawableTable data={JSON.parse(testCase.output)} />
                  ) : (
                    testCase.output
                  )}
                </OutputBlock>
              )}

              {testCase.expected_output && (
                <OutputBlock label='Expected output' status='info'>
                  {isDrawableTable(testCase.expected_output) ? (
                    <DrawableTable data={JSON.parse(testCase.expected_output)} />
                  ) : (
                    testCase.expected_output
                  )}
                </OutputBlock>
              )}
              {inputTabs && (
                <OutputBlock label='Test Input' status='info'>
                  <Tabs size='sm' color='white' tabs={inputTabs} />
                </OutputBlock>
              )}
              {!testCase.success && testCase.strict_row_order && (
                <Alert type='warning' icon={<InfoIcon />}>
                  This test case requires strict row order.
                </Alert>
              )}
            </>
          )}

          {testCase.monte_carlo_simulation &&
            isMonteCarloSimulation(testCase.expected_output, testCase.output) && (
              <>
                <MonteCarloSimulation
                  success={testCase.success}
                  expectedDistribution={JSON.parse(testCase.expected_output)}
                  outputDistribution={JSON.parse(testCase.output)}
                />
                <Alert type='warning' icon={<InfoIcon />}>
                  This test case is validated using a Monte Carlo simulation (N ={' '}
                  {testCase.monte_carlo_n}, ErrorThreshold = {testCase.monte_carlo_error_threshold}
                  ).
                </Alert>
              </>
            )}

          <Alert type='default'>
            Execution time:{' '}
            {testCase.runtime < 1000 ? `${testCase.runtime} ms` : `${testCase.runtime / 1000} s`}.
          </Alert>
        </div>
      )}
    </div>
  );
};

type Props = {
  index: number;
  testCase: TestCaseResult;
  collapsed?: boolean;
};
